<template>
  <div class="ats-bar">

    <!-- Left: title + asset chip + custom slot -->
    <div class="ats-left">
      <span v-if="title" class="ats-title">{{ title }}</span>

      <span v-if="assetName" class="ats-asset-chip">
        <v-icon v-if="assetIcon" size="13" style="margin-right:4px">{{ assetIcon }}</v-icon>
        {{ assetName }}
      </span>

      <slot name="left" />
    </div>

    <!-- Right: search + action buttons -->
    <div class="ats-right">

      <!-- Search -->
      <div v-if="search" class="u-search-bar ats-search">
        <v-icon size="16" color="grey">mdi-magnify</v-icon>
        <v-text-field
          :value="value"
          :placeholder="searchPlaceholder || 'Search…'"
          dense flat solo hide-details clearable
          class="u-search-input"
          @input="$emit('input', $event)"
        />
      </div>

      <!-- Custom actions slot (e.g. download-excel wrapper) -->
      <slot name="actions" />

      <!-- Icon buttons array -->
      <button
        v-for="(btn, i) in iconButtons"
        :key="`ib-${i}`"
        class="ec-icon-btn"
        :class="btn.variant ? `ec-icon-btn--${btn.variant}` : ''"
        :disabled="btn.disabled || btn.loading"
        :title="btn.title || ''"
        @click="btn.click && btn.click()"
      >
        <v-progress-circular v-if="btn.loading" size="13" width="2" indeterminate />
        <v-icon v-else :size="btn.size || 15">{{ btn.icon }}</v-icon>
      </button>

      <!-- Secondary (ghost) buttons array -->
      <button
        v-for="(btn, i) in secondaryButtons"
        :key="`sb-${i}`"
        class="ec-btn ec-btn--ghost ats-secondary-btn"
        :disabled="btn.disabled || btn.loading"
        @click="btn.click && btn.click()"
      >
        <v-progress-circular v-if="btn.loading" size="12" width="2" indeterminate style="margin-right:5px" />
        <v-icon v-else-if="btn.icon" size="14" style="margin-right:5px">{{ btn.icon }}</v-icon>
        {{ btn.label }}
      </button>

      <!-- Refresh button -->
      <button
        v-if="refreshButton"
        class="ec-icon-btn"
        :disabled="refreshLoading"
        title="Refresh"
        @click="$emit('refresh')"
      >
        <v-icon size="16" :class="{ 'ats-spin': refreshLoading }">mdi-sync</v-icon>
      </button>

      <!-- Primary button -->
      <button
        v-if="primaryButton"
        class="ec-btn ec-btn--primary"
        :disabled="primaryButton.disabled || primaryButton.loading"
        @click="primaryButton.click && primaryButton.click()"
      >
        <v-progress-circular
          v-if="primaryButton.loading"
          size="13" width="2" indeterminate
          style="margin-right:6px"
        />
        <v-icon v-else-if="primaryButton.icon" size="14" style="margin-right:5px">
          {{ primaryButton.icon }}
        </v-icon>
        {{ primaryButton.label }}
      </button>

      <!-- Close button -->
      <button
        v-if="closeButton"
        class="ec-icon-btn"
        title="Close"
        @click="$emit('close')"
      >
        <v-icon size="18">mdi-close</v-icon>
      </button>

    </div>
  </div>
</template>

<script>
export default {
  name: 'AdminTableSubheader',
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    /* Left side */
    title: { type: String, default: '' },
    assetName: { type: String, default: '' },
    assetIcon: { type: String, default: '' },

    /* Search */
    search: { type: Boolean, default: false },
    searchPlaceholder: { type: String, default: '' },
    value: { type: String, default: null }, // v-model

    /* Refresh */
    refreshButton: { type: Boolean, default: false },
    refreshLoading: { type: Boolean, default: false },

    /* Close */
    closeButton: { type: Boolean, default: false },

    /* Primary action button: { label, icon, click, loading, disabled } */
    primaryButton: { type: Object, default: null },

    /* Ghost/secondary buttons: [{ label, icon, click, loading, disabled }] */
    secondaryButtons: { type: Array, default: () => [] },

    /* Icon-only buttons: [{ icon, title, click, loading, disabled, variant, size }] */
    iconButtons: { type: Array, default: () => [] }
  }
}
</script>

<style scoped>
.ats-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  background: var(--ec-bg);
  border-bottom: 1px solid var(--ec-border);
  flex-wrap: wrap;
  min-height: 52px;
}

/* Left */
.ats-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.ats-title {
  font-size: .85rem;
  font-weight: 600;
  color: var(--ec-text-1);
  white-space: nowrap;
}
.ats-asset-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 20px;
  background: var(--ec-primary-bg);
  color: var(--ec-primary);
  font-size: .78rem;
  font-weight: 600;
  white-space: nowrap;
}

/* Right */
.ats-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.ats-search {
  max-width: 240px;
  margin-bottom: 0 !important; /* override u-search-bar global margin if any */
}

.ats-secondary-btn {
  padding: 6px 12px;
  font-size: .8rem;
}

/* Spin animation for refresh loading */
@keyframes ats-spin { to { transform: rotate(360deg); } }
.ats-spin { animation: ats-spin .7s linear infinite; display: inline-block; }
</style>
